import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { APP_VERSION, BUILD_TIMESTAMP } from '@/lib/version';

const LAST_SYNCED_VERSION_KEY = 'germanpath_last_synced_version';
const LAST_BUILD_TIMESTAMP_KEY = 'germanpath_last_build_timestamp';

/**
 * Automatically syncs data when a new app version OR build is detected.
 * Uses both version string AND build timestamp for reliable detection.
 * This helps users who have stale cache data after app updates.
 */
export function useAutoSync() {
  const queryClient = useQueryClient();
  const hasSynced = useRef(false);

  useEffect(() => {
    // Only run once per session
    if (hasSynced.current) return;
    
    const lastSyncedVersion = localStorage.getItem(LAST_SYNCED_VERSION_KEY);
    const lastBuildTimestamp = localStorage.getItem(LAST_BUILD_TIMESTAMP_KEY);
    
    // Detect new version OR new build (even if version didn't change)
    const isNewVersion = lastSyncedVersion && lastSyncedVersion !== APP_VERSION;
    const isNewBuild = lastBuildTimestamp && lastBuildTimestamp !== BUILD_TIMESTAMP;
    const isFirstVisit = !lastSyncedVersion && !lastBuildTimestamp;
    
    if (isNewVersion || isNewBuild) {
      hasSynced.current = true;
      
      const reason = isNewVersion 
        ? `version ${lastSyncedVersion} → ${APP_VERSION}` 
        : `new build detected`;
      console.log(`[AutoSync] Update detected (${reason}), syncing data...`);
      
      // Clear React Query cache
      queryClient.invalidateQueries();
      
      // Clear specific localStorage keys that might cause stale data issues
      const keysToPreserve = [
        'germanpath-ui-language',
        'germanpath_last_seen_version',
        LAST_SYNCED_VERSION_KEY,
        LAST_BUILD_TIMESTAMP_KEY,
        'theme',
      ];
      
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('germanpath_') && !keysToPreserve.includes(key)) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      // Refetch active queries
      queryClient.refetchQueries({ type: 'active' });
      
      // Update stored version and build timestamp
      localStorage.setItem(LAST_SYNCED_VERSION_KEY, APP_VERSION);
      localStorage.setItem(LAST_BUILD_TIMESTAMP_KEY, BUILD_TIMESTAMP);
      
      // Show a subtle notification
      toast.success('Daten synchronisiert', {
        description: `App wurde auf v${APP_VERSION} aktualisiert.`,
        duration: 3000,
      });
    } else if (isFirstVisit) {
      // First time user - just set both values
      localStorage.setItem(LAST_SYNCED_VERSION_KEY, APP_VERSION);
      localStorage.setItem(LAST_BUILD_TIMESTAMP_KEY, BUILD_TIMESTAMP);
    }
  }, [queryClient]);
}
